import { VStack } from '@chakra-ui/react'
import { NodeProps } from '@xyflow/react'
import React from 'react'

import { ExtensionInstance } from '@mangovoice/types'

import { TransferNodeData } from '../../converters/shared/reactFlowTypes'
import { useSwitchboardT } from '../../types'
import { text } from '../../utils/text'
import { ExtensionSelector, NodeInput } from '../core'
import { BaseNode, nodePropsAreEqual } from './BaseNode'
import { useTransferNode } from './hooks/useTransferNode'

export interface TransferAndContinueNodeProps extends NodeProps {
  extensionMap?: Map<number, ExtensionInstance>
  onDelete?: () => void
  onDeleteNode?: (nodeId: string) => void
  showDeleteButton?: boolean
  // Note: extensionsList is provided via Jotai atoms (extensionsListAtom)
  // Use useSwitchboardDataAtoms hook in the consuming app to set atoms
}

/**
 * Transfer and Continue node component for call flow diagrams.
 * Allows transferring a call to an extension with a timeout, then continuing to the next node.
 */
const TransferAndContinueNodeComponent: React.FC<
  TransferAndContinueNodeProps
> = ({
  id,
  data,
  extensionMap: _extensionMap,
  onDelete,
  onDeleteNode,
  showDeleteButton,
}) => {
  const nodeData = data as TransferNodeData & Record<string, unknown>
  const {
    currentExtensionId,
    timeout,
    handleExtensionChange,
    handleTimeoutChange,
    handleTimeoutBlur,
    hasData,
  } = useTransferNode(id, nodeData)
  const t = useSwitchboardT()

  return (
    <BaseNode
      label={text(
        t,
        'switchboard.nodeLabels.transfer_and_continue',
        'Transfer & Continue',
      )}
      showPlaceholder={!hasData}
      onDelete={onDelete}
      onDeleteNode={onDeleteNode}
      id={id}
      showDeleteButton={showDeleteButton}
    >
      <VStack align="stretch" gap={2}>
        <ExtensionSelector
          value={currentExtensionId}
          onChange={handleExtensionChange}
          filterMode="simple_dial"
        />
        <NodeInput
          label={text(t, 'switchboard.nodes.transfer.timeout', 'Timeout')}
          type="number"
          value={timeout || ''}
          onChange={(e) => handleTimeoutChange(e.target.value)}
          onBlur={handleTimeoutBlur}
          placeholder="20"
          min={5}
          max={300}
        />
      </VStack>
    </BaseNode>
  )
}

export const TransferAndContinueNode = React.memo(
  TransferAndContinueNodeComponent,
  nodePropsAreEqual,
)
